/**
 * Theme palettes for the public school landing page.
 *
 * Owners pick one of these six presets in /landing-settings. The selected
 * palette drives every accent on the public page (CTAs, badges, contact icons,
 * footer link, hero blobs).
 */
export const PALETTES = {
  blue: {
    label: "Ocean Blue",
    from: "#3B82F6",
    to: "#1D4ED8",
    hoverFrom: "#2563EB",
    hoverTo: "#1E40AF",
    text: "#1D4ED8",
    soft: "#DBEAFE",
    softer: "#EFF6FF",
    glow: "rgba(37, 99, 235, 0.55)",
    blobA: "#BFDBFE",
    blobB: "#DBEAFE",
  },
  orange: {
    label: "Sunny Orange",
    from: "#FB923C",
    to: "#EA580C",
    hoverFrom: "#F97316",
    hoverTo: "#C2410C",
    text: "#C2410C",
    soft: "#FFEDD5",
    softer: "#FFF7ED",
    glow: "rgba(234, 88, 12, 0.55)",
    blobA: "#FED7AA",
    blobB: "#FFEDD5",
  },
  pink: {
    label: "Cherry Pink",
    from: "#F472B6",
    to: "#BE185D",
    hoverFrom: "#EC4899",
    hoverTo: "#9D174D",
    text: "#BE185D",
    soft: "#FCE7F3",
    softer: "#FDF2F8",
    glow: "rgba(190, 24, 93, 0.55)",
    blobA: "#FBCFE8",
    blobB: "#FCE7F3",
  },
  purple: {
    label: "Royal Purple",
    from: "#A78BFA",
    to: "#6D28D9",
    hoverFrom: "#8B5CF6",
    hoverTo: "#5B21B6",
    text: "#6D28D9",
    soft: "#EDE9FE",
    softer: "#F5F3FF",
    glow: "rgba(109, 40, 217, 0.55)",
    blobA: "#DDD6FE",
    blobB: "#EDE9FE",
  },
  green: {
    label: "Forest Green",
    from: "#34D399",
    to: "#047857",
    hoverFrom: "#10B981",
    hoverTo: "#065F46",
    text: "#047857",
    soft: "#D1FAE5",
    softer: "#ECFDF5",
    glow: "rgba(4, 120, 87, 0.55)",
    blobA: "#A7F3D0",
    blobB: "#D1FAE5",
  },
  slate: {
    label: "Modern Slate",
    from: "#64748B",
    to: "#1E293B",
    hoverFrom: "#475569",
    hoverTo: "#0F172A",
    text: "#1E293B",
    soft: "#E2E8F0",
    softer: "#F1F5F9",
    glow: "rgba(30, 41, 59, 0.55)",
    blobA: "#CBD5E1",
    blobB: "#E2E8F0",
  },
  // ------- Extended palettes (14 added by user request) --------------------
  sky: {
    label: "Sky Blue",
    from: "#0EA5E9", to: "#0284C7", hoverFrom: "#0284C7", hoverTo: "#0369A1",
    text: "#0284C7", soft: "#E0F2FE", softer: "#F0F9FF",
    glow: "rgba(14, 165, 233, 0.55)", blobA: "#BAE6FD", blobB: "#E0F2FE",
  },
  aqua: {
    label: "Aqua Teal",
    from: "#06B6D4", to: "#0891B2", hoverFrom: "#0891B2", hoverTo: "#0E7490",
    text: "#0891B2", soft: "#CFFAFE", softer: "#ECFEFF",
    glow: "rgba(6, 182, 212, 0.55)", blobA: "#A5F3FC", blobB: "#CFFAFE",
  },
  mint: {
    label: "Fresh Mint",
    from: "#10B981", to: "#059669", hoverFrom: "#059669", hoverTo: "#047857",
    text: "#059669", soft: "#D1FAE5", softer: "#ECFDF5",
    glow: "rgba(16, 185, 129, 0.55)", blobA: "#A7F3D0", blobB: "#D1FAE5",
  },
  emerald: {
    label: "Emerald Glow",
    from: "#22C55E", to: "#16A34A", hoverFrom: "#16A34A", hoverTo: "#15803D",
    text: "#16A34A", soft: "#DCFCE7", softer: "#F0FDF4",
    glow: "rgba(34, 197, 94, 0.55)", blobA: "#BBF7D0", blobB: "#DCFCE7",
  },
  lime: {
    label: "Lemon Lime",
    from: "#84CC16", to: "#65A30D", hoverFrom: "#65A30D", hoverTo: "#4D7C0F",
    text: "#65A30D", soft: "#ECFCCB", softer: "#F7FEE7",
    glow: "rgba(132, 204, 22, 0.55)", blobA: "#D9F99D", blobB: "#ECFCCB",
  },
  amber: {
    label: "Golden Amber",
    from: "#F59E0B", to: "#D97706", hoverFrom: "#D97706", hoverTo: "#B45309",
    text: "#D97706", soft: "#FEF3C7", softer: "#FFFBEB",
    glow: "rgba(245, 158, 11, 0.55)", blobA: "#FDE68A", blobB: "#FEF3C7",
  },
  coral: {
    label: "Coral Peach",
    from: "#FB7185", to: "#E11D48", hoverFrom: "#E11D48", hoverTo: "#BE123C",
    text: "#E11D48", soft: "#FFE4E6", softer: "#FFF1F2",
    glow: "rgba(251, 113, 133, 0.55)", blobA: "#FECDD3", blobB: "#FFE4E6",
  },
  rose: {
    label: "Rose Blush",
    from: "#F472B6", to: "#DB2777", hoverFrom: "#DB2777", hoverTo: "#BE185D",
    text: "#DB2777", soft: "#FCE7F3", softer: "#FDF2F8",
    glow: "rgba(244, 114, 182, 0.55)", blobA: "#FBCFE8", blobB: "#FCE7F3",
  },
  magenta: {
    label: "Magenta Bloom",
    from: "#D946EF", to: "#C026D3", hoverFrom: "#C026D3", hoverTo: "#A21CAF",
    text: "#C026D3", soft: "#FAE8FF", softer: "#FDF4FF",
    glow: "rgba(217, 70, 239, 0.55)", blobA: "#F5D0FE", blobB: "#FAE8FF",
  },
  lavender: {
    label: "Lavender Mist",
    from: "#A78BFA", to: "#7C3AED", hoverFrom: "#7C3AED", hoverTo: "#6D28D9",
    text: "#7C3AED", soft: "#EDE9FE", softer: "#F5F3FF",
    glow: "rgba(167, 139, 250, 0.55)", blobA: "#DDD6FE", blobB: "#EDE9FE",
  },
  indigo: {
    label: "Indigo Dream",
    from: "#6366F1", to: "#4F46E5", hoverFrom: "#4F46E5", hoverTo: "#4338CA",
    text: "#4F46E5", soft: "#E0E7FF", softer: "#EEF2FF",
    glow: "rgba(99, 102, 241, 0.55)", blobA: "#C7D2FE", blobB: "#E0E7FF",
  },
  navy: {
    label: "Deep Navy",
    from: "#1E40AF", to: "#1E3A8A", hoverFrom: "#1E3A8A", hoverTo: "#172554",
    text: "#1E3A8A", soft: "#DBEAFE", softer: "#EFF6FF",
    glow: "rgba(30, 58, 138, 0.55)", blobA: "#BFDBFE", blobB: "#DBEAFE",
  },
  mocha: {
    label: "Mocha Brown",
    from: "#B45309", to: "#92400E", hoverFrom: "#92400E", hoverTo: "#78350F",
    text: "#92400E", soft: "#FEF3C7", softer: "#FFFBEB",
    glow: "rgba(146, 64, 14, 0.55)", blobA: "#FDE68A", blobB: "#FEF3C7",
  },
  charcoal: {
    label: "Elegant Charcoal",
    from: "#475569", to: "#334155", hoverFrom: "#334155", hoverTo: "#1E293B",
    text: "#334155", soft: "#E2E8F0", softer: "#F1F5F9",
    glow: "rgba(51, 65, 85, 0.55)", blobA: "#CBD5E1", blobB: "#E2E8F0",
  },
};

export const PALETTE_KEYS = Object.keys(PALETTES);

/* ---------- Custom hex → palette derivation ---------- */
function _hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}
function _rgbToHex({ r, g, b }) {
  const h = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}
function _shade({ r, g, b }, pct) {
  // pct > 0 → lighten toward white; pct < 0 → darken toward black
  if (pct >= 0) {
    return { r: r + (255 - r) * pct, g: g + (255 - g) * pct, b: b + (255 - b) * pct };
  }
  const k = 1 + pct; // pct is negative
  return { r: r * k, g: g * k, b: b * k };
}

/** Build a full palette object from a single #RRGGBB seed colour. */
export function paletteFromHex(hex) {
  const rgb = _hexToRgb(hex);
  if (!rgb) return null;
  const from     = _rgbToHex(rgb);
  const to       = _rgbToHex(_shade(rgb, -0.20));
  const hoverFrom= _rgbToHex(_shade(rgb, -0.08));
  const hoverTo  = _rgbToHex(_shade(rgb, -0.32));
  const text     = _rgbToHex(_shade(rgb, -0.30));
  const soft     = _rgbToHex(_shade(rgb,  0.78));
  const softer   = _rgbToHex(_shade(rgb,  0.92));
  const blobA    = _rgbToHex(_shade(rgb,  0.70));
  const blobB    = _rgbToHex(_shade(rgb,  0.85));
  const glow     = `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, 0.55)`;
  return {
    label: "Custom",
    from, to, hoverFrom, hoverTo, text, soft, softer, glow, blobA, blobB,
    custom_hex: from,
  };
}

export function getPalette(key) {
  if (key && typeof key === "string" && key.startsWith("#")) {
    return paletteFromHex(key) || PALETTES.blue;
  }
  return PALETTES[key] || PALETTES.blue;
}

/**
 * Convert markdown to safe HTML. Supports:
 *  - **bold**, *italic*, `code`, ~~strike~~
 *  - [text](url) links (opens in new tab)
 *  - bullet & numbered lists
 *  - Paragraph breaks on blank lines
 *  - Auto-escapes raw HTML to prevent XSS
 */
export function renderMarkdown(md) {
  if (!md || typeof md !== "string") return "";

  // 1. Escape HTML first to neutralise any <script> etc.
  let html = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  // 2. Inline marks
  html = html
    .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>")
    .replace(/~~([^~\n]+)~~/g, "<del>$1</del>")
    .replace(/`([^`\n]+)`/g, "<code class='px-1 rounded bg-black/10'>$1</code>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline hover:opacity-80">$1</a>');

  // 3. Lists + paragraphs
  const lines = html.split(/\r?\n/);
  const out = [];
  let listType = null;
  for (let line of lines) {
    const ul = /^\s*[-*]\s+(.+)$/.exec(line);
    const ol = /^\s*\d+\.\s+(.+)$/.exec(line);
    if (ul) {
      if (listType !== "ul") { if (listType) out.push(`</${listType}>`); out.push('<ul class="list-disc pl-6 space-y-1 my-2">'); listType = "ul"; }
      out.push(`<li>${ul[1]}</li>`);
    } else if (ol) {
      if (listType !== "ol") { if (listType) out.push(`</${listType}>`); out.push('<ol class="list-decimal pl-6 space-y-1 my-2">'); listType = "ol"; }
      out.push(`<li>${ol[1]}</li>`);
    } else {
      if (listType) { out.push(`</${listType}>`); listType = null; }
      if (line.trim() === "") out.push("<br/>");
      else out.push(`<p class="my-1.5">${line}</p>`);
    }
  }
  if (listType) out.push(`</${listType}>`);
  return out.join("");
}
